import React from 'react';
import { motion } from 'framer-motion';
import {
    DocumentTextIcon,
    ChatBubbleLeftRightIcon,
    UserGroupIcon,
    HeartIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    Squares2X2Icon
} from '@heroicons/react/24/outline';

const AdminDashboardStats = ({ stats }) => {
    // Calculate max value for chart scaling
    const maxActivity = stats.daily_activity ? Math.max(...stats.daily_activity.map(d => d.count)) : 0;

    const statCards = [
        {
            title: 'Traffic',
            subtitle: `Total Site Visits`,
            value: (stats.total_site_visits || 0).toLocaleString(),
            trend: stats.total_site_visits > 0 ? '+100%' : '0%', // Simplified trend for visits
            trendUp: true,
            trendText: `Across all pages`,
            icon: <Squares2X2Icon className="h-6 w-6" />,
            isDark: true
        },
        {
            title: 'Engagement',
            subtitle: `${stats.total_likes || 0} Likes & ${stats.total_comments || 0} Comments`,
            value: (stats.total_engagement || 0).toLocaleString(),
            trend: `${stats.comments_this_week > 0 ? '+' : ''}${stats.comments_this_week || 0}`,
            trendUp: true,
            trendText: `New comments this week`,
            icon: <ChatBubbleLeftRightIcon className="h-6 w-6" />,
            isDark: false
        },
        {
            title: 'Community',
            subtitle: `Registered Members`,
            value: (stats.total_users || 0).toLocaleString(),
            trend: `+${stats.new_users_this_week || 0}`,
            trendUp: true,
            trendText: `Joined this week`,
            icon: <UserGroupIcon className="h-6 w-6" />,
            isDark: false
        },
        {
            title: 'Inventory',
            subtitle: `${stats.total_blogs || 0} Articles`,
            value: (stats.total_products || 0).toLocaleString(),
            trend: `${stats.total_categories || 0} Categories`,
            trendUp: true,
            trendText: 'Total Products',
            icon: <DocumentTextIcon className="h-6 w-6" />,
            isDark: false
        }
    ];

    return (
        <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`p-6 rounded-3xl shadow-sm transition-all hover:shadow-md ${stat.isDark ? 'bg-[#B91C1C] text-white' : 'bg-white text-gray-800'
                            }`}
                    >
                        <div className="flex justify-between items-start mb-6">
                            <div className={`p-3 rounded-2xl ${stat.isDark ? 'bg-white/10' : 'bg-gray-100'
                                }`}>
                                {stat.icon}
                            </div>
                        </div>

                        <div className="space-y-1 mb-6">
                            <h3 className={`text-sm font-medium ${stat.isDark ? 'text-white/90' : 'text-gray-500'}`}>
                                {stat.title}
                            </h3>
                            <p className={`text-xs ${stat.isDark ? 'text-white/60' : 'text-gray-400'}`}>
                                {stat.subtitle}
                            </p>
                        </div>

                        <div className="flex items-end justify-between">
                            <h2 className="text-3xl font-bold">{stat.value}</h2>
                        </div>

                        <div className="mt-4 flex items-center space-x-2 text-sm">
                            <span className={`flex items-center font-semibold ${stat.trendUp ? (stat.isDark ? 'text-white' : 'text-[#72B33A]') : (stat.isDark ? 'text-white/80' : 'text-[#B91C1C]')
                                }`}>
                                {stat.trendUp ? <ArrowTrendingUpIcon className="h-4 w-4 mr-1" /> : <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />}
                                {stat.trend}
                            </span>
                            <span className={`${stat.isDark ? 'text-white/60' : 'text-gray-400'}`}>
                                {stat.trendText}
                            </span>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white p-6 rounded-3xl shadow-sm">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-bold text-gray-800">Engagement Overview</h3>
                        <div className="flex space-x-2">
                            <button className="px-3 py-1 text-sm border rounded-full hover:bg-gray-50">Last 12 Days</button>
                        </div>
                    </div>
                    <div className="h-64 flex items-end justify-between space-x-2 px-4 border-b border-gray-100 pb-12">
                        {/* Activity Bars */}
                        {stats.daily_activity && stats.daily_activity.map((day, i) => {
                            const heightPercentage = maxActivity > 0 ? (day.count / maxActivity) * 80 + 10 : 10;
                            return (
                                <div key={i} className="w-full bg-gray-50 rounded-t-lg relative group h-full flex flex-col items-center justify-end">
                                    <motion.div
                                        initial={{ height: 0 }}
                                        animate={{ height: `${heightPercentage}%` }}
                                        transition={{ duration: 0.8, delay: i * 0.05, ease: "easeOut" }}
                                        className="w-full bg-[#333333]/10 rounded-t-lg transition-all duration-500 group-hover:bg-[#B91C1C] relative cursor-pointer"
                                    >
                                        <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 shadow-xl text-white text-xs px-2 py-1.5 rounded-lg whitespace-nowrap z-10">
                                            {day.count} interactions
                                        </div>
                                    </motion.div>
                                    <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 text-[10px] text-gray-400 rotate-45 origin-left whitespace-nowrap">
                                        {day.date}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="bg-white p-6 rounded-3xl shadow-sm">
                    <h3 className="text-lg font-bold text-gray-800 mb-6">Most Popular Posts</h3>
                    <div className="relative h-48 w-48 mx-auto mb-10">
                        <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
                            <path className="text-gray-100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3.2" />
                            {stats.top_blogs && stats.top_blogs.length > 0 && (
                                <path
                                    className="text-[#B91C1C]"
                                    strokeDasharray={`${stats.top_blogs[0].percentage}, 100`}
                                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="3.2"
                                    strokeLinecap="round"
                                />
                            )}
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center flex-col">
                            <span className="text-2xl font-bold text-gray-800">
                                {stats.total_engagement || 0}
                            </span>
                            <span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mt-1">Total Hits</span>
                        </div>
                    </div>
                    <div className="space-y-4">
                        {stats.top_blogs && stats.top_blogs.slice(0, 3).map((blog, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="space-y-2"
                            >
                                <div className="flex items-center justify-between text-xs mb-1">
                                    <div className="flex items-center truncate max-w-[80%]">
                                        <span className={`w-2 h-2 ${index === 0 ? 'bg-gray-800' : index === 1 ? 'bg-gray-400' : 'bg-gray-200'} rounded-full mr-2 flex-shrink-0`}></span>
                                        <span className="text-gray-600 truncate font-medium">{blog.name}</span>
                                    </div>
                                    <span className="font-bold text-gray-900">{blog.percentage}%</span>
                                </div>
                                <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${blog.percentage}%` }}
                                        transition={{ duration: 1, delay: 0.5 + (index * 0.1) }}
                                        className={`${index === 0 ? 'bg-gray-800' : index === 1 ? 'bg-gray-400' : 'bg-gray-200'} h-full rounded-full`}
                                    />
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboardStats;
